import {
    COUNTRY_COUNT_AMERICAS,
    COUNTRY_COUNT_ASIA,
    COUNTRY_COUNT_AFRICA,
    COUNTRY_COUNT_EUROPE,
    COUNTRY_COUNT_OCEANIA,
    TAXONOMIES_LEGAL_SOURCES,
    DEBUG_MODE
} from '../constants';
import { debugRun } from '../debug/debug.utils';
import {
    CountryProfile,
    Page,
    Taxonomies,
    Taxonomy
} from '../types';
import {
    assertIsArray,
    assertPropertyExists,
    fallbackValue,
    typeArray,
    typeNumber,
    typeString,
    typeStringBoolean,
    typeStringDatetime,
    typeStringAssetPath,
} from './api.typeguards';
import { DataParser } from './api.types';

const parseArrayDataHandler = <T>(
    dataParser: DataParser<T>
): DataParser<T[]> => {
    return (data) => {
        const result: T[] = [];
        const items = assertIsArray(data);

        try {
            items.forEach(dataItem => {
                try {
                    const parsedItem = dataParser(dataItem);
                    result.push(parsedItem);
                }
                catch (error) {
                    if (DEBUG_MODE) {
                        console.error('Error: ' + error + '. Skipping object', dataItem);
                    }
                    else {
                        console.warn('Warning: Skipped object');
                    }
                }
            });
        }
        catch (error) {
            debugRun(({ error }) => {
                error(error);
            });
        }

        return result;
    };
};

const parseCountryProfile: DataParser<CountryProfile> = data => ({
    countryId: assertPropertyExists(data, 'country', typeNumber),
    typeOfQuotaIds: typeArray(assertPropertyExists(data, 'type_of_legislated_quotas')).map(typeNumber),
    sanctionId: assertPropertyExists(data, 'sanctions_for_non_compliance', typeNumber),
    electoralSystemTypeId: assertPropertyExists(data, 'electoral_system_type', typeNumber),
    rankingPlacementId: assertPropertyExists(data, 'ranking_placement', typeNumber),
    rankingPlacementDetails: assertPropertyExists(data, 'ranking_placement_details', typeString),
    correctiveMeasureDetail: assertPropertyExists(data, 'corrective_measure_detail', typeString),
    methodOfFillingSeats: assertPropertyExists(data, 'method_of_filling_seats', typeString),
    quotaTarget: assertPropertyExists(data, 'quota_target', typeNumber),
    womensRepresentation: assertPropertyExists(data, 'womens_representation', fallbackValue(typeNumber, null)),
    date: assertPropertyExists(data, 'date', typeString),
    datetime: assertPropertyExists(data, 'date', typeStringDatetime),
    ipuDate: assertPropertyExists(data, 'ipu_date', typeString),
    regionId: assertPropertyExists(data, 'm49_region', typeNumber),
    otherSanctions: assertPropertyExists(data, 'other_sanctions', typeString),
    listsAreRejected: assertPropertyExists(data, 'lists_are_rejected', typeString),
    electoralSystemSubType: assertPropertyExists(data, 'electoral_system_sub_type', typeString),
    comments: assertPropertyExists(data, 'comments', typeString),
    quotaTargetMetId: assertPropertyExists(data, 'quota_target_met', typeNumber),
    quotaForInputOnlyId: assertPropertyExists(data, 'quota_for_input_only', typeNumber),
    parliamentStructureId: assertPropertyExists(data, 'parliament_structure', typeNumber),

    constitutionProvisionsGenderQuotaId: assertPropertyExists(data, 'constitution_provisions_gender_quota', typeNumber),
    constitutionLegalSource: assertPropertyExists(data, 'constitution_legal_source', typeString),
    constitutionLegalProvisions: assertPropertyExists(data, 'constitution_legal_provisions', typeString),

    electoralProvisionsGenderQuotaId: assertPropertyExists(data, 'electoral_provisions_gender_quota', typeNumber),
    electoralLegalSource: assertPropertyExists(data, 'electoral_legal_source', typeString),
    electoralLegalProvisions: assertPropertyExists(data, 'electoral_legal_provisions', typeString),

    partyProvisionsGenderQuotaId: assertPropertyExists(data, 'party_provisions_gender_quota', typeNumber),
    partyLegalSource: assertPropertyExists(data, 'party_legal_source', typeString),
    partyLegalProvisions: assertPropertyExists(data, 'party_legal_provisions', typeString),

    parityProvisionsGenderQuotaId: assertPropertyExists(data, 'parity_provisions_gender_quota', typeNumber),
    parityLegalSource: assertPropertyExists(data, 'parity_legal_source', typeString),
    parityLegalProvisions: assertPropertyExists(data, 'parity_legal_provisions', typeString),

    financingProvisionsGenderQuotaId: assertPropertyExists(data, 'financing_provisions_gender_quota', typeNumber),
    financingLegalSource: assertPropertyExists(data, 'financing_legal_source', typeString),
    financingLegalProvisions: assertPropertyExists(data, 'financing_legal_provisions', typeString),

    politicalProvisionsGenderQuotaId: assertPropertyExists(data, 'political_provisions_gender_quota', typeNumber),
    politicalLegalSource: assertPropertyExists(data, 'political_legal_source', typeString),
    politicalLegalProvisions: assertPropertyExists(data, 'political_legal_provisions', typeString),

    localProvisionsGenderQuotaId: assertPropertyExists(data, 'local_provisions_gender_quota', typeNumber),
    localLegalSource: assertPropertyExists(data, 'local_legal_source', typeString),
    localLegalProvisions: assertPropertyExists(data, 'local_legal_provisions', typeString),

    localLevel: assertPropertyExists(data, 'local_level', typeStringBoolean),
    quotaApplication: assertPropertyExists(data, 'quota_application', typeString),
});

const parsePage: DataParser<Page> = data => ({
    name: assertPropertyExists(data, 'title', typeString),
    html: assertPropertyExists(data, 'text', String),
    imageUrl: assertPropertyExists(data, 'image', fallbackValue(typeStringAssetPath, '')),
});

const parseTaxonomy: DataParser<Taxonomy> = data => ({
    id: assertPropertyExists(data,'id', typeNumber),
    name: assertPropertyExists(data, 'name', typeString),
    prefix: assertPropertyExists(data, 'prefix', fallbackValue(typeString, null)),
});

export const parseCountryProfiles = parseArrayDataHandler(parseCountryProfile);

export const parsePages = parseArrayDataHandler(parsePage);

export const parseTaxonomyGroups: DataParser<Taxonomies> = data => {
    const result: Taxonomies = {
        dates: [],
        countries: [],
        typesOfQuota: [],
        sanctions: [],
        electoralSystemTypes: [],
        rankingPlacement: [],
        legalSources: [...TAXONOMIES_LEGAL_SOURCES],
        regions: [],
        quotaForInputOnly: [],
        parliamentStructure: [],
        quotaTargetMet: [],
        provisionsOnGenderQuotas_yesNo: []
    };

    assertIsArray(data).forEach(data => {
        try {
            const taxonomyType = assertPropertyExists(data, 'taxonomy', String);
            const taxonomy = parseTaxonomy(data);

            switch(taxonomyType) {
                case 'Dropdown Dates':
                    return result.dates.push({
                        ...taxonomy,
                        datetime: typeStringDatetime(taxonomy.name)
                    });

                case 'Country':
                    return result.countries.push({
                        ...taxonomy,
                        hasLocalLevelData: false,
                        hasNationalLevelData: false,
                        regionId: 0,
                    });

                case 'Type of legislated quotas':
                    return result.typesOfQuota.push(taxonomy);

                case 'Sanctions for non compliance':
                    return result.sanctions.push(taxonomy);

                case 'Electoral System':
                    return result.electoralSystemTypes.push(taxonomy);

                case 'Ranking/placement':
                    return result.rankingPlacement.push(taxonomy);

                case 'Quota for input only':
                    return result.quotaForInputOnly.push(taxonomy);

                case 'Parliament structure':
                    return result.parliamentStructure.push(taxonomy);

                case 'Quota target met':
                    return result.quotaTargetMet.push(taxonomy);

                case 'M49 Region':
                    return result.regions.push((() => {
                        let countryCount = 0;

                        switch(taxonomy.id) {
                            case 240: countryCount = COUNTRY_COUNT_AMERICAS; break;
                            case 241: countryCount = COUNTRY_COUNT_ASIA; break;
                            case 239: countryCount = COUNTRY_COUNT_AFRICA; break;
                            case 238: countryCount = COUNTRY_COUNT_EUROPE; break;
                            case 242: countryCount = COUNTRY_COUNT_OCEANIA; break;
                        }

                        return {
                            ...taxonomy,
                            countryCount
                        };
                    })());
            }

            const taxonomiesToSortAsc: Array<keyof Taxonomies> = [
                'countries', 'typesOfQuota',
            ];

            const taxonomiesToSortDesc: Array<keyof Taxonomies> = [
                'dates',
            ];

            taxonomiesToSortAsc.forEach(taxonomyName => {
                result[taxonomyName].sort((a, b) => a.name > b.name ? 1 : -1);
            });

            taxonomiesToSortDesc.forEach(taxonomyName => {
                result[taxonomyName].sort((a, b) => a.name > b.name ? -1 : 1);
            });
        }
        catch (error) {
            console.warn(`Skipped taxonomy. Reason: ${error}`);
        }
    });

    return result;
};
